import { useState, useEffect } from "react";
import { adminApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Search, Building2, MoreVertical, CheckCircle, XCircle } from "lucide-react";

const AdminCompaniesPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await adminApi.getCompanies();
      setCompanies(response.data);
    } catch (error) {
      toast.error("Error al cargar empresas");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubscription = async (companyId, status, plan = null) => {
    try {
      await adminApi.updateSubscription(companyId, { status, plan });
      toast.success("Suscripción actualizada");
      loadCompanies();
    } catch (error) {
      toast.error("Error al actualizar suscripción");
    }
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = 
      company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.rfc.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || company.subscription_status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B9BD5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-companies-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Empresas</h1>
        <p className="text-slate-600">Administra todas las empresas registradas</p>
      </div>

      {/* Filters */}
      <Card className="cia-card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Buscar por nombre, RFC o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 cia-input"
                data-testid="search-companies-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="active">Activas</SelectItem>
                <SelectItem value="trial">En Prueba</SelectItem>
                <SelectItem value="inactive">Inactivas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Companies table */}
      <Card className="cia-card">
        <CardContent className="p-0">
          {filteredCompanies.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No hay empresas</h3>
              <p className="text-slate-500">
                {searchTerm || statusFilter !== "all" 
                  ? "No se encontraron empresas con los filtros aplicados"
                  : "Aún no hay empresas registradas"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Empresa</th>
                    <th>RFC</th>
                    <th>Email</th>
                    <th>Plan</th>
                    <th>Estado</th>
                    <th>Fecha Registro</th>
                    <th className="text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCompanies.map((company) => (
                    <tr key={company.id} data-testid={`company-row-${company.id}`}>
                      <td>
                        <div className="flex items-center gap-3">
                          {company.logo_url ? (
                            <img src={company.logo_url} alt="" className="w-8 h-8 rounded object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                              <Building2 className="w-4 h-4 text-slate-400" />
                            </div>
                          )}
                          <span className="font-medium">{company.name}</span>
                        </div>
                      </td>
                      <td className="font-mono text-sm">{company.rfc}</td>
                      <td>{company.email}</td>
                      <td>{company.subscription_plan || '-'}</td>
                      <td>
                        <span className={company.subscription_status === 'active' ? 'badge-paid' : 'badge-pending'}>
                          {company.subscription_status === 'active' ? 'Activa' : company.subscription_status === 'trial' ? 'Prueba' : 'Inactiva'}
                        </span>
                      </td>
                      <td className="text-slate-500">
                        {new Date(company.created_at).toLocaleDateString('es-MX')}
                      </td>
                      <td className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`company-actions-${company.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem 
                              onClick={() => handleUpdateSubscription(company.id, 'active', 'professional')}
                            >
                              <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                              Activar Plan Profesional
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleUpdateSubscription(company.id, 'inactive')}
                              className="text-red-600"
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Desactivar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCompaniesPage;
